import React, { useEffect, useState, useRef, createElement } from 'react';
import { UrgencyBar } from '../components/UrgencyBar';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { TestimonialCard } from '../components/TestimonialCard';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { FAQSection } from '../components/FAQSection';
import { Check, ArrowRight, AlertTriangle, Sprout, BookOpen, ShieldCheck, ChefHat, Thermometer, Recycle, Package, Gift } from 'lucide-react';
// Meta Pixel ID
const META_PIXEL_ID = '841702842124464';
// Declare fbq type for TypeScript
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
export function LandingPage() {
  const purchaseSectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Preconnect to CDN for faster resource loading (~140ms savings)
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = 'https://cdn.magicpatterns.com';
    preconnect.crossOrigin = 'anonymous';
    document.head.insertBefore(preconnect, document.head.firstChild);
    // DNS prefetch as fallback for browsers that don't support preconnect
    const dnsPrefetch = document.createElement('link');
    dnsPrefetch.rel = 'dns-prefetch';
    dnsPrefetch.href = 'https://cdn.magicpatterns.com';
    document.head.insertBefore(dnsPrefetch, preconnect.nextSibling);
    // Preload LCP image for faster discovery
    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'image';
    preloadLink.href = 'https://cdn.magicpatterns.com/uploads/vRhyfC1EMFCYwPtmD6XxjJ/heylloyd-image-1772744892126_(1).png';
    preloadLink.setAttribute('fetchpriority', 'high');
    document.head.insertBefore(preloadLink, dnsPrefetch.nextSibling);
    document.title = 'MANUAL DE CULTIVO DE GIRGOLAS';
    // Set Open Graph meta tags for link sharing
    const setMetaTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    setMetaTag('og:title', 'MANUAL DE CULTIVO DE GIRGOLAS');
    setMetaTag('og:description', 'Tu primera cosecha de Gírgolas en casa, sin equipos caros ni experiencia previa.');
    setMetaTag('og:image', 'https://cdn.magicpatterns.com/uploads/vRhyfC1EMFCYwPtmD6XxjJ/heylloyd-image-1772744892126_(1).png');
    setMetaTag('og:type', 'website');
    // Initialize Meta Pixel (deferred to avoid blocking initial render)
    const pixelTimer = setTimeout(() => {
      if (!window.fbq) {
        const n = window.fbq = function (...args: any[]) {
          n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
        } as any;
        if (!window._fbq) window._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        // Load the Facebook Pixel script
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';
        document.head.appendChild(script);
        // Add noscript fallback
        const noscript = document.createElement('noscript');
        const img = document.createElement('img');
        img.height = 1;
        img.width = 1;
        img.style.display = 'none';
        img.src = `https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`;
        noscript.appendChild(img);
        document.body.appendChild(noscript);
      }
      // Initialize pixel and track PageView
      window.fbq('init', META_PIXEL_ID);
      window.fbq('track', 'PageView');
    }, 1500);
    return () => clearTimeout(pixelTimer);
  }, []);
  // Track InitiateCheckout event
  const trackInitiateCheckout = (value: number, contentName: string) => {
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        value: value,
        currency: 'ARS',
        content_name: contentName
      });
    }
  };
  const scrollToPurchase = () => {
    purchaseSectionRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  // Handle checkout click with tracking
  const handleCheckoutClick = (url: string, value: number, comboName: string) => {
    trackInitiateCheckout(value, comboName);
    // Small delay to ensure the event is sent before redirect
    setTimeout(() => {
      window.location.href = url;
    }, 300);
  };
  const CHECKOUT_URL = 'https://checkout.rosetadigital.com/cart/45956509139097:1';
  const CHECKOUT_URL_COMBO2 = 'https://checkout.rosetadigital.com/cart/46097784144025:1';
  const CHECKOUT_URL_COMBO3 = 'https://checkout.rosetadigital.com/cart/46097809146009:1';
  const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/vRhyfC1EMFCYwPtmD6XxjJ/heylloyd-image-1772744892126_(1).png';
  const testimonials = [{
    name: 'Camila Rodríguez',
    location: 'Córdoba, Capital',
    text: 'Nunca había plantado ni un potus. La guía es súper clara, seguí los pasos y a las 3 semanas ya tenía mis primeras gírgolas. ¡Increíble verlas crecer!',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }, {
    name: 'Roberto Méndez',
    location: 'CABA',
    text: 'Vivo en un monoambiente y pensaba que no tenía espacio. El bonus del mini invernadero me salvó. Estoy produciendo en un rincón de la cocina.',
    image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }, {
    name: 'Laura Fernández',
    location: 'Rosario',
    text: 'Había intentado con videos de YouTube y se me contaminó todo. Con esta guía entendí qué hacía mal. La sección de problemas y soluciones es oro puro.',
    image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }, {
    name: 'Esteban Ruiz',
    location: 'Mendoza',
    text: 'Lo compré por curiosidad y ahora es mi hobby favorito. Las recetas del final son buenísimas, nunca había cocinado gírgolas así.',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }, {
    name: 'Patricia Gómez',
    location: 'La Plata',
    text: 'Excelente inversión. Solo con lo que ahorré en no comprar kits caros ya se pagó sola la guía. Muy recomendada para empezar de cero.',
    image: 'https://images.unsplash.com/photo-1554151228-14d9def656ec?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }];
  const faqItems = [{
    question: '¿Es difícil conseguir los materiales?',
    answer: 'No. El 90% lo tenés en tu casa (cartón, aserrín, paja) y el micelio se pide online en Mercado Libre con un clic. Te explicamos exactamente cómo.'
  }, {
    question: '¿Necesito experiencia previa en cultivo?',
    answer: 'Para nada. La guía está diseñada para principiantes absolutos. Cada paso está explicado con detalle, fotos y checklists.'
  }, {
    question: '¿Cómo recibo el material?',
    answer: 'Es 100% digital. Inmediatamente después del pago recibís los PDFs por email. Sin esperas, sin envío físico, sin costos adicionales.'
  }, {
    question: '¿Funciona en departamentos o espacios chicos?',
    answer: 'Sí. Incluimos un bonus con 3 diseños de mini invernadero casero adaptados a espacios reducidos.'
  }, {
    question: '¿Qué pasa si algo sale mal en mi cultivo?',
    answer: 'La guía incluye una sección completa de problemas y soluciones. Sabés exactamente qué ajustar si algo no va como esperabas.'
  }];
  return <div className="min-h-screen bg-white font-['Inter','Inter_Fallback',sans-serif] text-[#4A4A4A] selection:bg-[#3D5A3D] selection:text-white" data-id="element-44">
      <UrgencyBar data-id="element-45" />
      <PurchaseNotification data-id="element-46" />
      <WhatsAppButton data-id="element-47" />

      {/* Hero Section */}
      <header className="bg-white pt-16 pb-12 px-4 relative overflow-hidden border-b border-stone-100" data-id="element-48">
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-49">
          <span className="inline-block text-[#C4704A] font-bold tracking-widest text-xs md:text-sm mb-6 uppercase font-['Inter'] bg-[#C4704A]/10 px-4 py-1.5 rounded-full" data-id="element-50">
            MÉTODO PROBADO EN ARGENTINA
          </span>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-['Inter'] font-extrabold text-[#1A1A1A] leading-tight mb-6 tracking-tight" data-id="element-51">
            EL MÉTODO PARA CULTIVAR GÍRGOLAS EN CASA
          </h1>

          {/* Product Image */}
          <div className="mb-8 flex justify-center relative" data-id="element-52">
            <div className="absolute inset-0 bg-[#3D5A3D] blur-[100px] opacity-10 rounded-full transform scale-75" data-id="element-53"></div>
            <img src={PRODUCT_IMAGE} alt="Manual de Cultivo de Gírgolas" className="w-full max-w-md mx-auto drop-shadow-2xl relative z-10" fetchPriority="high" width={665} height={1191} decoding="async" style={{
            aspectRatio: '665 / 1191'
          }} data-id="element-54" />
          </div>

          <p className="text-lg md:text-xl text-[#4A4A4A] mb-10 max-w-2xl mx-auto font-medium leading-relaxed" data-id="element-55">
            Aprendé a transformar cartón, paja o aserrín en alimento gourmet.
            Sin equipos caros, usando lo que ya tenés a mano.
          </p>

          <button onClick={scrollToPurchase} className="group bg-[#2D5A27] text-white hover:bg-[#1f401b] transition-all duration-300 font-['Inter'] font-bold text-lg md:text-xl py-5 px-10 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center gap-3 mx-auto" data-id="element-56">
            QUIERO EMPEZAR HOY
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-57" />
          </button>

          <p className="mt-6 text-sm font-bold text-[#3D5A3D] flex items-center justify-center gap-2" data-id="element-58">
            <Check className="w-4 h-4" data-id="element-59" />
            ENTREGA DIGITAL INSTANTÁNEA · SIN GASTOS DE ENVÍO
          </p>
        </div>
      </header>

      {/* 3-Step Protocol */}
      <section className="py-14 px-4 bg-[#FAFAF8]" data-id="element-60">
        <div className="max-w-5xl mx-auto" data-id="element-61">
          <h2 className="text-2xl md:text-3xl font-['Inter'] font-extrabold text-center text-[#1A1A1A] mb-8" data-id="element-62">
            El método en 3 pasos
          </h2>

          <div className="grid md:grid-cols-3 gap-4" data-id="element-63">
            {[{
            num: 1,
            icon: Recycle,
            title: 'Sustratos Reciclados',
            tag: 'Bajo costo',
            desc: 'Cartón, paja o aserrín. Materiales que ya tenés en casa.'
          }, {
            num: 2,
            icon: Sprout,
            title: 'Inoculación Simple',
            tag: 'Micelio vía ML',
            desc: 'Comprás micelio en Mercado Libre y lo recibís en tu casa.'
          }, {
            num: 3,
            icon: ChefHat,
            title: 'Cosecha en 21 Días',
            tag: 'Resultados rápidos',
            desc: 'Seguís el método y en 3 semanas tenés gírgolas frescas.'
          }].map(step => <div key={step.num} className="bg-white p-5 rounded-xl border border-stone-200 text-center" data-id="element-64">
                <div className="flex items-center justify-center gap-2 mb-3" data-id="element-65">
                  <div className="w-8 h-8 bg-[#3D5A3D] text-white rounded-full flex items-center justify-center font-bold text-sm font-['Inter']" data-id="element-66">
                    {step.num}
                  </div>
                  <step.icon className="w-5 h-5 text-[#3D5A3D]" data-id="element-67" />
                </div>
                <h3 className="font-['Inter'] font-bold text-base text-[#1A1A1A] mb-1" data-id="element-68">
                  {step.title}
                </h3>
                <span className="inline-block text-[#4A4A4A] text-[11px] font-bold px-2 py-0.5 rounded-full mb-2 bg-[#FAFAF8] border border-stone-200" data-id="element-69">
                  {step.tag}
                </span>
                <p className="text-[#4A4A4A] text-sm leading-snug" data-id="element-70">
                  {step.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="pt-14 pb-10 px-4 bg-white" data-id="element-71">
        <div className="max-w-3xl mx-auto" data-id="element-72">
          <div data-id="element-73">
            <h2 className="text-3xl md:text-4xl font-['Inter'] font-extrabold text-[#1A1A1A] mb-6 leading-tight" data-id="element-74">
              El manual que te lleva de cero a tu primera cosecha
            </h2>
            <p className="text-lg text-[#4A4A4A] mb-6 leading-relaxed" data-id="element-75">
              Creamos el{' '}
              <strong className="text-[#1A1A1A] font-['Inter']" data-id="element-76">
                MANUAL DE CULTIVO DE GÍRGOLAS EN CASA
              </strong>{' '}
              para que puedas hacerlo bien desde el primer intento.
            </p>
            <p className="text-lg text-[#4A4A4A] mb-8 leading-relaxed" data-id="element-77">
              Un PDF práctico que te lleva paso a paso por todo el proceso, con
              materiales reales, costos reales y condiciones posibles para
              cualquier hogar.
            </p>
            <div className="bg-[#FAFAF8] p-6 rounded-xl border-l-4 border-[#3D5A3D] shadow-sm" data-id="element-78">
              <p className="font-bold text-[#1A1A1A] text-lg flex items-center gap-3 font-['Inter']" data-id="element-79">
                <Check className="text-[#3D5A3D] w-6 h-6 flex-shrink-0" data-id="element-80" />
                Aunque nunca hayas cultivado hongos antes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="pt-10 pb-14 px-4 bg-[#FAFAF8]" data-id="element-81">
        <div className="max-w-4xl mx-auto" data-id="element-82">
          <h2 className="text-2xl md:text-3xl font-['Inter'] font-extrabold text-center text-[#1A1A1A] mb-8 flex items-center justify-center gap-3" data-id="element-83">
            <BookOpen className="w-6 h-6 text-[#3D5A3D]" data-id="element-84" />
            <span data-id="element-85">¿Qué incluye el manual?</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-3" data-id="element-86">
            {['Proceso completo: sustratos, inoculación, incubación, fructificación y cosecha.', 'Producción con residuos del hogar.', 'Sistemas anti-contaminación fáciles de aplicar.', 'Tablas de rendimiento y mezclas según clima y sustrato.', 'Guía de problemas y soluciones.', 'Recetas y valor nutricional de tu cosecha.'].map((item, idx) => <div key={idx} className="flex gap-3 items-start p-4 bg-white rounded-xl border border-stone-200" data-id="element-87">
                <Check className="w-4 h-4 text-[#3D5A3D] mt-0.5 flex-shrink-0" data-id="element-88" />
                <p className="text-[#4A4A4A] text-sm leading-snug" data-id="element-89">{item}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 px-4 bg-[#2D3B2D] text-white" data-id="element-90">
        <div className="max-w-5xl mx-auto" data-id="element-91">
          <h2 className="text-2xl md:text-3xl font-['Inter'] font-extrabold text-center mb-8 text-white" data-id="element-92">
            ¿Qué vas a lograr?
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-93">
            {[{
            icon: BookOpen,
            text: 'Entender todo el proceso desde el día uno.'
          }, {
            icon: ShieldCheck,
            text: 'Cultivar sin miedo a contaminar.'
          }, {
            icon: Check,
            text: 'Ahorrar tiempo y dinero evitando errores.'
          }, {
            icon: Sprout,
            text: 'Tu primera cosecha sin frustraciones.'
          }, {
            icon: Thermometer,
            text: 'Floradas más grandes y rendidoras.'
          }, {
            icon: ChefHat,
            text: 'Transformar residuos en alimento real.'
          }].map((benefit, idx) => <div key={idx} className="bg-white/5 p-4 rounded-xl border border-white/10 flex items-start gap-3" data-id="element-94">
                <benefit.icon className="w-5 h-5 text-[#C4704A] flex-shrink-0 mt-0.5" data-id="element-95" />
                <p className="text-sm leading-snug text-stone-200" data-id="element-96">
                  {benefit.text}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-14 px-4 bg-white" data-id="element-97">
        <div className="max-w-4xl mx-auto" data-id="element-98">
          <h2 className="text-2xl md:text-3xl font-['Inter'] font-extrabold text-center text-[#1A1A1A] mb-8" data-id="element-99">
            Bonus incluidos con tu compra
          </h2>

          <div className="space-y-3" data-id="element-100">
            {[{
            title: 'Mini Invernadero Casero',
            desc: 'Planos y medidas exactas. 3 versiones según tu espacio.',
            price: '$15.000'
          }, {
            title: 'Conservar y deshidratar gírgolas',
            desc: 'Guardá tu cosecha por meses: deshidratado, congelado y más.',
            price: '$12.000'
          }, {
            title: 'Economía del cultivo casero',
            desc: 'Cuánto produce, cuánto gastás y cómo escalar sin invertir de más.',
            price: '$10.000'
          }].map((bonus, idx) => <div key={idx} className="bg-[#FAFAF8] px-5 py-4 rounded-xl border border-stone-200 flex items-center gap-4" data-id="element-101">
                <Gift className="w-5 h-5 text-[#C4704A] flex-shrink-0" data-id="element-102" />
                <div className="flex-grow min-w-0" data-id="element-103">
                  <h3 className="text-sm font-['Inter'] font-bold text-[#1A1A1A]" data-id="element-104">
                    {bonus.title}
                  </h3>
                  <p className="text-[#4A4A4A] text-xs leading-snug mt-0.5" data-id="element-105">
                    {bonus.desc}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0" data-id="element-106">
                  <span className="line-through text-stone-400 text-xs" data-id="element-107">
                    {bonus.price}
                  </span>
                  <span className="text-[#22C55E] font-extrabold text-sm font-['Inter']" data-id="element-108">
                    GRATIS
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* INSUMOS ANTI-ABANDONO SECTION (CRITICAL) */}
      <section className="py-10 px-4 bg-[#3D5A3D] text-white border-y-4 border-[#C4704A]" data-id="element-109">
        <div className="max-w-3xl mx-auto text-center" data-id="element-110">
          <div className="flex items-center justify-center gap-3 mb-3" data-id="element-111">
            <Package className="w-7 h-7 text-[#C4704A]" data-id="element-112" />
            <h2 className="text-xl md:text-2xl font-['Inter'] font-extrabold text-white" data-id="element-113">
              ¿DÓNDE CONSIGO LAS SEMILLAS?
            </h2>
          </div>
          <p className="text-sm md:text-base leading-relaxed text-stone-200 mb-4 max-w-2xl mx-auto" data-id="element-114">
            Buscá <strong className="text-white" data-id="element-115">"micelio de gírgolas"</strong>{' '}
            en Mercado Libre. Hay muchos proveedores y lo recibís en tu
            domicilio.
          </p>
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg border border-white/20 text-sm" data-id="element-116">
            <Check className="w-4 h-4 text-[#C4704A]" data-id="element-117" />
            <span className="font-bold font-['Inter']" data-id="element-118">
              Todo se consigue online con un clic
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section ref={purchaseSectionRef} className="py-14 px-4 bg-[#FAFAF8]" data-id="element-119">
        <div className="max-w-3xl mx-auto" data-id="element-120">
          <h2 className="text-2xl md:text-3xl font-['Inter'] font-extrabold mb-2 text-center text-[#1A1A1A]" data-id="element-121">
            Elegí tu combo
          </h2>
          <p className="text-base text-[#4A4A4A] mb-8 text-center" data-id="element-122">
            Todos los materiales son digitales y de acceso inmediato
          </p>

          <ComboSelector handleCheckoutClick={handleCheckoutClick} CHECKOUT_URL={CHECKOUT_URL} CHECKOUT_URL_COMBO2={CHECKOUT_URL_COMBO2} CHECKOUT_URL_COMBO3={CHECKOUT_URL_COMBO3} data-id="element-123" />

          {/* Payment info */}
          <div className="max-w-2xl mx-auto mt-8" data-id="element-124">
            <div className="flex items-center justify-center gap-3 mb-5" data-id="element-125">
              <ShieldCheck className="w-4 h-4 text-[#3D5A3D]" data-id="element-126" />
              <p className="text-xs text-[#1A1A1A] font-bold font-['Inter']" data-id="element-127">
                Pago 100% seguro procesado por:
              </p>
              <img src="https://cdn.magicpatterns.com/uploads/pvyXgBqyPZQk88jP3yrfEU/ML.jpg" alt="Mercado Libre" className="h-6 object-contain opacity-80" data-id="element-128" />
            </div>

            <div className="bg-white border border-stone-200 rounded-lg p-4 text-left flex gap-3" data-id="element-129">
              <AlertTriangle className="w-4 h-4 text-[#C4704A] mt-0.5 flex-shrink-0" data-id="element-130" />
              <p className="text-xs text-[#4A4A4A] leading-relaxed" data-id="element-131">
                <span className="font-bold text-[#1A1A1A]" data-id="element-132">Importante:</span> No
                necesitás cuenta en Mercado Pago. Podés pagar con tarjeta de
                crédito o débito.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 bg-white border-t border-stone-100" data-id="element-133">
        <div className="max-w-6xl mx-auto" data-id="element-134">
          <h2 className="text-3xl md:text-4xl font-['Inter'] font-extrabold text-center text-[#1A1A1A] mb-16" data-id="element-135">
            Lo que dicen quienes ya están cultivando
          </h2>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-136">
            {testimonials.slice(0, 3).map((t, i) => <div key={i} className="h-full" data-id="element-137">
                <TestimonialCard {...t} data-id="element-138" />
              </div>)}
          </div>
          <div className="grid md:grid-cols-2 gap-8 mt-8 max-w-4xl mx-auto" data-id="element-139">
            {testimonials.slice(3, 5).map((t, i) => <div key={i} className="h-full" data-id="element-140">
                <TestimonialCard {...t} data-id="element-141" />
              </div>)}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-4 bg-[#FAFAF8] border-t border-stone-100" data-id="element-142">
        <div className="max-w-4xl mx-auto" data-id="element-143">
          <h2 className="text-3xl md:text-4xl font-['Inter'] font-extrabold text-center text-[#1A1A1A] mb-16" data-id="element-144">
            Preguntas frecuentes
          </h2>
          <FAQSection items={faqItems} data-id="element-145" />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2D3B2D] text-stone-400 py-12 text-center text-sm" data-id="element-146">
        <div className="max-w-4xl mx-auto px-4" data-id="element-147">
          <div className="flex items-center justify-center gap-2 mb-6 text-white" data-id="element-148">
            <ShieldCheck className="w-5 h-5 text-[#C4704A]" data-id="element-149" />
            <span className="font-bold font-['Inter']" data-id="element-150">Pago 100% Seguro</span>
          </div>
          <p className="mb-4 font-medium text-stone-300" data-id="element-151">
            © {new Date().getFullYear()} Cultivo Casero. Todos los derechos
            reservados.
          </p>
          <p className="opacity-60 text-xs" data-id="element-152">
            Este producto no garantiza resultados económicos. Los resultados
            pueden variar.
          </p>
        </div>
      </footer>
    </div>;
}
function ComboSelector({
  handleCheckoutClick,
  CHECKOUT_URL,
  CHECKOUT_URL_COMBO2,
  CHECKOUT_URL_COMBO3
}: {
  handleCheckoutClick: (url: string, value: number, comboName: string) => void;
  CHECKOUT_URL: string;
  CHECKOUT_URL_COMBO2: string;
  CHECKOUT_URL_COMBO3: string;
}) {
  const [selected, setSelected] = useState(0);
  const combos = [{
    id: 0,
    name: 'COMBO 2',
    title: 'De Casa a Escala Comercial',
    includes: ['Manual de Cultivo de Gírgolas en Casa (guía paso a paso)', 'Bonus: Mini Invernadero Casero (3 diseños)', 'Bonus: Conservar y deshidratar gírgolas', 'Bonus: Economía del cultivo casero', 'Manual de Producción Comercial de Gírgolas', 'Guía de leyes y aspectos legales para vender'],
    price: '$29.990',
    value: 29990,
    trackName: 'COMBO 2 - Producción de Gírgolas',
    url: CHECKOUT_URL_COMBO2,
    badge: '⭐ RECOMENDADO',
    highlighted: true
  }, {
    id: 1,
    name: 'COMBO 1',
    title: 'Cultivo de Gírgolas en Casa',
    includes: ['Manual de Cultivo de Gírgolas en Casa (guía paso a paso)', 'Bonus: Mini Invernadero Casero (3 diseños)', 'Bonus: Conservar y deshidratar gírgolas', 'Bonus: Economía del cultivo casero'],
    price: '$19.990',
    value: 19990,
    trackName: 'COMBO 1 - Cultivo de Gírgolas en Casa',
    url: CHECKOUT_URL,
    badge: null,
    highlighted: false
  }, {
    id: 2,
    name: 'COMBO 3',
    title: 'Sistema Completo Profesional',
    includes: ['Manual de Cultivo de Gírgolas en Casa (guía paso a paso)', 'Bonus: Mini Invernadero Casero (3 diseños)', 'Bonus: Conservar y deshidratar gírgolas', 'Bonus: Economía del cultivo casero', 'Manual de Producción Comercial de Gírgolas', 'Guía de leyes y aspectos legales para vender', 'Manual de Higiene, Control y Anticontaminación', 'Protocolos profesionales de esterilización'],
    price: '$39.990',
    value: 39990,
    trackName: 'COMBO 3 - Sistema Completo de Producción',
    url: CHECKOUT_URL_COMBO3,
    badge: null,
    highlighted: false
  }];
  const current = combos[selected];
  return <>
      <div className="bg-white rounded-xl border border-stone-200 shadow-md overflow-hidden" data-id="element-153">
        {/* Option Selector */}
        <div className="divide-y divide-stone-100" data-id="element-154">
          {combos.map(combo => <button key={combo.id} onClick={() => setSelected(combo.id)} className={`w-full text-left px-5 py-4 flex items-center gap-3 transition-colors ${selected === combo.id ? combo.highlighted ? 'bg-[#3D5A3D]/10 border-l-4 border-l-[#2D5A27]' : 'bg-[#3D5A3D]/5 border-l-4 border-l-[#2D5A27]' : combo.highlighted ? 'bg-[#FAFAF8] border-l-4 border-l-transparent' : 'border-l-4 border-l-transparent hover:bg-[#FAFAF8]'}`} data-id="element-155">
              {/* Radio indicator */}
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${selected === combo.id ? 'border-[#2D5A27] bg-[#2D5A27]' : 'border-stone-300'}`} data-id="element-156">
                {selected === combo.id && <div className="w-1.5 h-1.5 bg-white rounded-full" data-id="element-157"></div>}
              </div>

              {/* Content */}
              <div className="flex-grow min-w-0" data-id="element-158">
                <div className="flex items-center gap-2 flex-wrap" data-id="element-159">
                  <span className={`font-['Inter'] font-bold text-sm ${combo.highlighted ? 'text-[#2D5A27]' : 'text-[#1A1A1A]'}`} data-id="element-160">
                    {combo.name}
                  </span>
                  {combo.badge && <span className="bg-[#C4704A] text-white text-[10px] font-bold px-2 py-0.5 rounded-full font-['Inter']" data-id="element-161">
                      {combo.badge}
                    </span>}
                </div>
                <p className="text-[#4A4A4A] text-xs mt-0.5" data-id="element-162">{combo.title}</p>
              </div>

              {/* Price */}
              <span className={`font-['Inter'] font-extrabold text-lg flex-shrink-0 ${combo.highlighted ? 'text-[#2D5A27]' : 'text-[#1A1A1A]'}`} data-id="element-163">
                {combo.price}
              </span>
            </button>)}
        </div>

        {/* Selected Combo Details */}
        <div className="border-t border-stone-200 px-5 py-5 bg-[#FAFAF8]" data-id="element-164">
          <p className="font-['Inter'] font-bold text-[#1A1A1A] text-xs mb-2" data-id="element-165">
            {current.name} incluye:
          </p>
          <ul className="space-y-1.5 mb-4" data-id="element-166">
            {current.includes.map((item, idx) => <li key={idx} className="flex items-start gap-2 text-xs text-[#4A4A4A]" data-id="element-167">
                <Check className="w-3.5 h-3.5 text-[#3D5A3D] mt-0.5 flex-shrink-0" data-id="element-168" />
                <span className="leading-snug" data-id="element-169">{item}</span>
              </li>)}
          </ul>

          <div className="flex items-center gap-2 text-[11px] text-[#3D5A3D] font-bold mb-4" data-id="element-170">
            <Check className="w-3.5 h-3.5" data-id="element-171" />
            ENTREGA DIGITAL INSTANTÁNEA · SIN GASTOS DE ENVÍO
          </div>

          <button onClick={() => handleCheckoutClick(current.url, current.value, current.trackName)} className="w-full bg-[#2D5A27] text-white hover:bg-[#1f401b] transition-colors font-['Inter'] font-bold text-base py-3.5 px-6 rounded-xl shadow-md flex items-center justify-center gap-2" data-id="element-172">
            QUIERO EMPEZAR HOY
            <ArrowRight className="w-5 h-5" data-id="element-173" />
          </button>
        </div>
      </div>
    </>;
}
function XIcon() {
  return <>
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-174">
        <path d="M18 6 6 18" data-id="element-175" />
        <path d="m6 6 12 12" data-id="element-176" />
      </svg>
    </>;
}